import { Layer } from "./Layer.js";
import { DataFrame } from "../../data/DataFrame.js"
import { getColorFromScale, copyColorScale } from "../../ui/color/Utils.js";
import { filterDataFrame } from "../../plot/Utils.js";
import { FeatureLayerUtils } from "./utils/FeatureLayerUtils.js";

export { MeasurementsLayer }

/**
 * Layer for displaying Measurements from a DataFrame Object
 * 
 * @author rhess <robin.hess@awi.de>
 * @memberof vef.map.layer
 */
class MeasurementsLayer extends Layer {

    /**
     * Set all properties for the layer based on the options object.
     * 
     * @param {object} config 
     * @param {object} cache 
     * @param {string} id 
     */
    constructor(config, cache, id) {
        // calling parent constructor
        super(config, cache, id);

        // init default values and define getters and setters
        this.addDefaults_({
            opacityRatio: 1,
            hiddenColumns: [],
            defaultXAxis: "",
            defaultYAxis: "",
            invertXAxis: false,
            invertYAxis: false,
            groupByColumn: "",
            columnData: "",
            columnLatitude: "latitude",
            columnLongitude: "longitude",
            // overwrite existing defaults
            metadataTemplate: "#measurements_layer",
            availableCrs: FeatureLayerUtils.availableCrs,
            styleSettings: false,
            opacitySettings: true,
            colorScaleSettings: true,
            icon: "fas fa-draw-polygon"
        });

        this.setAll_("type", "measurement");

        // remove availableCrs to always use default
        if ("availableCrs" in this.config) delete this.config.availableCrs;
        if ("availableCrs" in this.cache) delete this.cache.availableCrs;

        // override defaults based on other properties
        if (!this.defaults.title) this.defaults.title = this.columnData;

        this.dataFrameOriginal = null;
        this.dataFrame = null;

        // private properties
        this.clickedFeatures_ = [];
        this.activeGeoJSON_ = null;

        if (config.dataFrame) this.setDataFrame(config.dataFrame);
    }

    /**
     * Create a map layer based on the service options
     * 
     * @param {string} type e.g. "leaflet"
     * @private
     */
    createMapLayer_(type) {
        switch (type) {
            case "leaflet":
                const layer = L.geoJSON(null, {
                    onEachFeature: (feature, layer) => {
                        layer.on("click", e => {
                            this.clickedFeatures_.push(feature);
                        });
                    },
                    style: (feature) => {
                        if (!(this.columnData in feature.properties)) return {};
                        return this.getColorOptions_(feature.properties[this.columnData])
                    },
                    pointToLayer: (geoJsonPoint, latlng) => {
                        return L.circleMarker(latlng, { renderer: FeatureLayerUtils.getCanvasRenderer() });
                    }
                })

                this.mapLayers_[type] = layer;
                return layer;
        }
        return null;
    }

    setDataFrame(dataFrame, groupByColumn) {
        if (groupByColumn) this.groupByColumn = groupByColumn;
        if (dataFrame instanceof DataFrame) {
            this.dataFrameOriginal = dataFrame;

            if (this.groupByColumn) {
                if (typeof this.groupByColumn === 'string') {
                    this.dataFrame = filterDataFrame(dataFrame, this.groupByColumn);
                } else if (typeof this.groupByColumn === 'function') {
                    this.dataFrame = dataFrame.filter(this.groupByColumn);
                }
            } else {
                this.dataFrame = dataFrame;
            }
        } else {
            throw new Error("invalid dataFrame");
        }
    }

    applyFilter_() {
        let dataFrame = this.dataFrame;

        if (this.filter) {
            const cols = dataFrame.columnMap;
            const timeColumn = cols["Date/Time"] || cols["date/time"] || cols["datetime"] || cols["date_time"] || cols["time"];

            dataFrame = dataFrame.filter((i, row) => {
                for (let i = 0; i < this.filter.length; ++i) {
                    // dont apply filter if the layer is excluded
                    if (this.filter[i].excludedLayers.includes(this.uniqueId)) continue;

                    let type = this.filter[i].type.toLowerCase();
                    let column = (type == "time") ? timeColumn : cols[this.filter[i].column];

                    if (Number.isFinite(column)) {
                        for (let j = 0; j < this.filter[i].values.length; ++j) {
                            const values = this.filter[i].values[j];
                            const dataFrameVal = ((type == "time")) ? new Date(row[column]).getTime() : row[column];
                            const filterVal1 = ((type == "time")) ? new Date(values[1]).getTime() : values[1];

                            // checks are inverted, to return false if they don't match
                            switch (values[0].toLowerCase()) {
                                case "bt":
                                    const filterVal2 = ((type == "time")) ? new Date(values[2]).getTime() : values[2];
                                    if ((dataFrameVal < filterVal1) || (dataFrameVal > filterVal2)) return false;
                                    break;
                                case "eq":
                                    if (dataFrameVal != filterVal1) return false;
                                    break;
                                case "lt":
                                    if (dataFrameVal > filterVal1) return false;
                                    break;
                                case "gt":
                                    if (dataFrameVal < filterVal1) return false;
                                    break;
                            }
                        }
                    }
                }

                return true;
            });
        }

        return new Promise(resolve => resolve(dataFrame));
    }

    /**
     * helper method for finding a column index containing a certain set of strings
     * 
     * @param {DataFrame} dataFrame to search for columns
     * @param {string | string[]} names single column name or an array of names
     */
    getColumnIndex(dataFrame, names) {
        if (!Array.isArray(names)) names = [names];
        // first iteration for exact matches
        for (let name of names) {
            if (name && (name in dataFrame.columnMap)) return dataFrame.columnMap[name];
        }
        // second iteration for checking containing strings
        for (let col in dataFrame.columnMap) {
            for (let name of names) {
                if (name && (col.toLowerCase().includes(name.toLowerCase()))) return dataFrame.columnMap[col];
            }
        }
        return null;
    }

    /**
     * helper method for finding a column name from an index
     * 
     * @param {DataFrame} dataFrame to search for columns
     * @param {number} index
     */
    getColumnName(dataFrame, index) {
        for (let col in dataFrame.columnMap) {
            if (dataFrame.columnMap[col] == index) return col;
        }
        return null;
    }

    /**
     * Helper method to update the mapLayer according to
     * the current state. Might be necessary after switching the map type
     * 
     * @private
     */
    updateMapLayer_(type) {
        this.activeMap_ = type;
        this.applyFilter();
    }

    async createGeoJSON_() {
        this.activeGeoJSON_ = null;

        this.fire("loading");
        let dataFrame = await this.applyFilter_();
        this.fire("load");

        const cols = dataFrame.columnMap;
        const rows = dataFrame.rows;
        const colIndex = this.getColumnIndex(dataFrame, this.columnData);
        const lat = this.getColumnIndex(dataFrame, [this.columnLatitude, "Latitude", "latitude", "lat"]);
        const lng = this.getColumnIndex(dataFrame, [this.columnLongitude, "Longitude", "longitude", "lng"]);

        if ((typeof lat != "number") || (typeof lng != "number")) {
            console.warn("invalid lat/lon");
            return;
        }
        if (typeof colIndex != "number") {
            console.warn("invalid columnData");
            return;
        }

        const geoJSON = {
            type: "FeatureCollection",
            features: []
        };

        for (let i = 0; i < rows.length; ++i) {
            const row = rows[i];
            if (!Number.isFinite(row[lat]) || !Number.isFinite(row[lng])) continue;

            const feature = {
                type: "Feature",
                geometry: {
                    type: "Point",
                    coordinates: [row[lng], row[lat]]
                },
                properties: {},
                _data: row
            }

            for (let key in cols) {
                feature.properties[key] = rows[i][cols[key]];
            }

            geoJSON.features.push(feature);
        }

        this.activeGeoJSON_ = geoJSON;
    }

    applyGeoJSON_() {
        if (!(this.activeMap_ in this.mapLayers_) || !this.activeGeoJSON_) return;

        switch (this.activeMap_) {
            case "leaflet":
                this.mapLayers_.leaflet.clearLayers();
                this.mapLayers_.leaflet.addData(this.activeGeoJSON_);
                break;
        }
    }

    /**
     * Set on the layer. Uses generic
     * filter syntax and transforms it internally
     * 
     * @override
     */
    applyFilter() {
        this.createGeoJSON_().then(() => this.applyGeoJSON_());
    }

    /**
     * Get Template for printing the layer metadata in a table
     * Property names are mapped for better readability.
     * @override
     */
    getMetadataTemplate() {
        const properties = super.getMetadataTemplate();

        properties["Layer Information"]["Column Name"] = this.columnData;

        if (this.dataFrame && this.dataFrame.description) {
            const description = this.dataFrame.description;
            properties["Dataset Description"] = {
                "Citation": description.citation,
                "URL": description.url,
                "License": description.license,
                "Events": (description.events) ? Object.keys(description.events).join(", ") : null,
                "Parameters": (description.parameters) ? description.parameters.join(", ") : null,
                "Projects": (description.projects) ? description.projects.join(", ") : null,
                "Further Details": (description.relations) ? description.relations.furtherDetails.join(", ") : null,
                "Related To": (description.relations) ? description.relations.relatedTo.join(", ") : null
            };
        }

        return properties;
    }

    /**
     * get the color options for a single marker
     * @private
     */
    getColorOptions_(value) {
        if (Number.isFinite(value) && this.colorScale && (this.colorScale.length > 0)) {
            const color = getColorFromScale(this.colorScale, value);
            return {
                color: color.color,
                opacity: this.opacity,
                fillOpacity: this.opacity * this.opacityRatio,
                stroke: false
            }
        } else {
            return {
                color: '#3388ff',
                opacity: this.opacity,
                fillOpacity: this.opacity * this.opacityRatio,
                stroke: false
            }
        }
    }

    /**
     * Set the colorscale for the layer
     * 
     * @param colorScale 
     */
    setColorScale(colorScale) {
        this.colorScale = colorScale;

        if (!(this.activeMap_ in this.mapLayers_)) return;

        switch (this.activeMap_) {
            case "leaflet":
                this.mapLayers_.leaflet.eachLayer(layer => {
                    if (this.columnData in layer.feature.properties) {
                        const value = layer.feature.properties[this.columnData];
                        const style = this.getColorOptions_(value);
                        layer.setStyle(style)
                    }
                });
                break;
        }
    }

    /**
     * Get the active colorscale for the layer
     */
    getColorScale() {
        return (this.colorScale) ? copyColorScale(this.colorScale) : null;
    }

    getOptions() {
        const options = super.getOptions();
        options.type = "measurement";
        return options;
    }

    /**
     * Get Info of the last clicked features
     * @returns {Promise} feature info
     */
    getFeatureInfo() {
        return FeatureLayerUtils.getFeatureInfo(this);
    }

    /**
     * Set the transparency between 0 and 1
     * @override
     * @param {number} opacity
     */
    setOpacity(opacity) {
        FeatureLayerUtils.setOpacity(this, opacity);
    }

    /**
     * Get the bounding box as an object
     * 
     * @returns {object} { min: {x, y}, max: {x, y}, crs: "CRS:84" }
     */
    getBounds() {
        const bounds = FeatureLayerUtils.computeBounds(this);
        return (bounds) ? bounds : super.getBounds();
    }
}